import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { Header } from './components/layout/Header'
import { Footer } from './components/layout/Footer'
import { ScrollToTop } from './components/layout/ScrollToTop'
import { PlatformShell } from './components/layout/PlatformShell'
import { Hero } from './components/sections/Hero'
import { SolutionAreas } from './components/sections/SolutionAreas'
import { PainTestimonials } from './components/sections/PainTestimonials'
import { SystemPillars } from './components/sections/SystemPillars'
import { StartingPoints } from './components/sections/StartingPoints'
import { HowWeWorkTimeline } from './components/sections/HowWeWorkTimeline'
import { TechStack } from './components/sections/TechStack'
import { FAQ } from './components/sections/FAQ'
import { FinalCTA } from './components/sections/FinalCTA'
import { LoginForm } from './components/admin/LoginForm'
import { AdminLayout } from './components/admin/AdminLayout'
import { LeadsList } from './components/admin/LeadsList'
import { FaqsEditor } from './components/admin/FaqsEditor'
import { AreasEditor } from './components/admin/AreasEditor'
import { BrainPage } from './pages/platform/BrainPage'
import { BuildPage } from './pages/platform/BuildPage'
import { GovernPage } from './pages/platform/GovernPage'
import { HowWeWorkPage } from './pages/HowWeWorkPage'
import { WorkspaceSelectorPage } from './pages/knowledge/WorkspaceSelectorPage'
import { KnowledgeLayout } from './components/knowledge/KnowledgeLayout'
import { IngestPage } from './pages/knowledge/IngestPage'
import { VaultPage } from './pages/knowledge/VaultPage'
import { SearchPage } from './pages/knowledge/SearchPage'
import type { AuthUser } from './types'

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (credentials: { email: string; password: string }) => Promise<boolean>
  logout: () => void
}

function LandingPage() {
  return (
    <>
      <Header />
      <main className="flex-1">
        <Hero />
        <SolutionAreas />
        <PainTestimonials />
        <SystemPillars />
        <StartingPoints />
        <HowWeWorkTimeline />
        <TechStack />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </>
  )
}

function AdminLoginPage({ auth }: { auth: AuthState }) {
  const navigate = useNavigate()

  if (auth.isAuthenticated) {
    return <Navigate to="/admin" replace />
  }

  const handleLogin = async (credentials: { email: string; password: string }) => {
    const success = await auth.login(credentials)
    if (success) {
      navigate('/admin', { replace: true })
    }
    return success
  }

  return <LoginForm onSubmit={handleLogin} error={auth.error} isLoading={auth.isLoading} />
}

function AdminRoutes({ auth }: { auth: AuthState }) {
  if (auth.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      </div>
    )
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/admin/login" replace />
  }

  return (
    <AdminLayout user={auth.user} onLogout={auth.logout}>
      <Routes>
        <Route index element={<LeadsList />} />
        <Route path="leads" element={<LeadsList />} />
        <Route path="faqs" element={<FaqsEditor />} />
        <Route path="areas" element={<AreasEditor />} />
      </Routes>
    </AdminLayout>
  )
}

function ProtectedRoute({ auth, children }: { auth: AuthState; children: React.ReactNode }) {
  if (auth.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      </div>
    )
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/admin/login" replace />
  }

  return <>{children}</>
}

function WorkspaceSelectorRoute({ auth }: { auth: AuthState }) {
  return (
    <ProtectedRoute auth={auth}>
      <WorkspaceSelectorPage />
    </ProtectedRoute>
  )
}

function KnowledgeRoutes({ auth }: { auth: AuthState }) {
  return (
    <ProtectedRoute auth={auth}>
      <Outlet />
    </ProtectedRoute>
  )
}

function PlatformLayout() {
  return (
    <PlatformShell>
      <Outlet />
    </PlatformShell>
  )
}

function App() {
  const auth = useAuth()

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="flex min-h-screen flex-col bg-bg">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/how-we-work" element={<HowWeWorkPage />} />
          <Route path="/plataforma" element={<PlatformLayout />}>
            <Route path="brain" element={<BrainPage />} />
            <Route path="build" element={<BuildPage />} />
            <Route path="govern" element={<GovernPage />} />
          </Route>
          <Route path="/admin/login" element={<AdminLoginPage auth={auth} />} />
          <Route path="/admin/*" element={<AdminRoutes auth={auth} />} />
          <Route path="/workspace-selector" element={<WorkspaceSelectorRoute auth={auth} />} />
          <Route path="/app" element={<KnowledgeRoutes auth={auth} />}>
            <Route element={<KnowledgeLayout />}>
              <Route index element={<Navigate to="/app/ingest" replace />} />
              <Route path="ingest" element={<IngestPage />} />
              <Route path="vault" element={<VaultPage />} />
              <Route path="search" element={<SearchPage />} />
            </Route>
          </Route>
        </Routes>
      </div>
    </BrowserRouter>
  )
}

export default App
